'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import { useParams } from 'next/navigation';
import { API, getUserImageUrl } from '@/config/env';
import { api, ApiError } from '@/lib/api';
import { getApiErrorMessage } from '@/lib/errors';
import type { ApiSuccessResponse, Collection, UserCollectionItem, UserProfileData } from '@/shared/types';
import { getItemCoverImage, getItemDisplayBrand, getItemDisplayName, getItemDisplayScale, isReservedRoute } from '@/shared';

type ItemPagination = { page: number; limit: number; total: number; totalPages: number };
type Tab = 'items' | 'collections';

export default function UserProfilePage() {
  const params = useParams();
  const username = params.username as string;

  const [user, setUser] = useState<UserProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<Tab>('items');
  const [itemsPage, setItemsPage] = useState(1);
  const [collectionsPage, setCollectionsPage] = useState(1);

  const itemsPerPage = 12;
  const collectionsPerPage = 12;

  const fetchProfile = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      if (isReservedRoute(username)) {
        setError('This username is reserved and not available.');
        return;
      }

      const result = await api.get<ApiSuccessResponse<UserProfileData>>(API.USER_PROFILE(username), {
        modelsPage: itemsPage,
        modelsLimit: itemsPerPage,
        collectionsPage,
        collectionsLimit: collectionsPerPage,
      });

      setUser(result.data);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setError('Collector profile not found.');
      } else {
        setError(getApiErrorMessage(err));
      }
    } finally {
      setLoading(false);
    }
  }, [username, itemsPage, collectionsPage]);

  useEffect(() => {
    if (username) {
      fetchProfile();
    }
  }, [username, fetchProfile]);

  const joinDate = useMemo(() => {
    if (!user) return '';
    return new Date(user.createdAt).toLocaleDateString();
  }, [user]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
          <div className="flex justify-center" aria-busy="true">
            <div className="skeleton-brand h-3 w-36 rounded-full" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
          <div className="card-shell p-8 text-center">
            <p className="text-lg font-semibold text-destructive">{error}</p>
            <p className="mt-2 text-sm text-muted-foreground">Try checking the username or explore other public showcases.</p>
            <div className="mt-5">
              <Link href="/explore" className="btn-primary">
                Explore collections
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <section className="card-shell overflow-hidden">
          <div className="bg-gradient-to-r from-primary/10 via-brand-orange/10 to-transparent px-6 py-8 sm:px-8">
            <div className="flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-4">
                <div className="h-20 w-20 overflow-hidden rounded-full border-4 border-border bg-card">
                  {user.avatar ? (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img src={user.avatar} alt={user.name} className="h-full w-full object-cover" />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center bg-primary text-2xl font-bold text-white">
                      {user.name.charAt(0).toUpperCase()}
                    </div>
                  )}
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.14em] text-primary">Collector profile</p>
                  <h1 className="text-3xl font-extrabold tracking-tight text-foreground">{user.name}</h1>
                  <p className="text-sm text-muted-foreground">@{user.username}</p>
                </div>
              </div>
              <p className="spec-text text-muted-foreground">Member since {joinDate}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 border-t border-border sm:grid-cols-4">
            <StatCell label="Items" value={String(user.stats.totalItems)} />
            <StatCell label="Collections" value={String(user.stats.totalCollections)} />
            <StatCell label="Owned" value={String(user.stats.totalItems)} />
            <StatCell label="Wishlist" value="0" />
          </div>
        </section>

        <section className="mt-8 card-shell">
          <div className="flex border-b border-border px-4 sm:px-6">
            <TabButton
              active={activeTab === 'items'}
              label="Items"
              count={user.stats.totalItems}
              onClick={() => {
                setActiveTab('items');
                setItemsPage(1);
              }}
            />
            <TabButton
              active={activeTab === 'collections'}
              label="Collections"
              count={user.stats.totalCollections}
              onClick={() => {
                setActiveTab('collections');
                setCollectionsPage(1);
              }}
            />
          </div>

          <div className="p-4 sm:p-6">
            {activeTab === 'items' ? (
              <ItemsPanel
                items={user.items}
                pagination={user.pagination.items}
                currentPage={itemsPage}
                onPageChange={setItemsPage}
              />
            ) : (
              <CollectionsPanel
                collections={user.collections}
                pagination={user.pagination.collections}
                username={username}
                currentPage={collectionsPage}
                onPageChange={setCollectionsPage}
              />
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

function StatCell({ label, value }: { label: string; value: string }) {
  return (
    <div className="border-r border-border px-4 py-4 last:border-r-0">
      <p className="spec-text text-primary">{value}</p>
      <p className="text-xs uppercase tracking-[0.12em] text-muted-foreground">{label}</p>
    </div>
  );
}

function TabButton({
  active,
  label,
  count,
  onClick,
}: {
  active: boolean;
  label: string;
  count: number;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`mr-2 border-b-2 px-3 py-4 text-sm font-semibold transition-colors ${
        active ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
      }`}
    >
      {label}
      <span className="ml-2 rounded-full bg-muted px-2 py-0.5 text-xs text-foreground">{count}</span>
    </button>
  );
}

function ItemsPanel({
  items,
  pagination,
  currentPage,
  onPageChange,
}: {
  items: UserCollectionItem[];
  pagination: ItemPagination;
  currentPage: number;
  onPageChange: (page: number) => void;
}) {
  if (items.length === 0) {
    return <p className="py-10 text-center text-sm text-muted-foreground">No items in this showcase yet.</p>;
  }

  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((item) => (
          <ItemCard key={item.id} item={item} />
        ))}
      </div>
      {pagination.totalPages > 1 && (
        <div className="mt-6">
          <Pagination currentPage={currentPage} totalPages={pagination.totalPages} onPageChange={onPageChange} />
        </div>
      )}
    </>
  );
}

function ItemCard({ item }: { item: UserCollectionItem }) {
  const cover = getItemCoverImage(item);
  const imageUrl = cover ? getUserImageUrl(cover.r2Key) : item.catalogProduct?.imageUrl ?? null;
  const safeImageUrl = imageUrl?.startsWith('http://') ? imageUrl.replace('http://', 'https://') : imageUrl;

  return (
    <article className="card-shell overflow-hidden">
      <div className="aspect-[4/3] bg-muted">
        {safeImageUrl ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img src={safeImageUrl} alt={getItemDisplayName(item)} className="h-full w-full object-cover" loading="lazy" referrerPolicy="no-referrer" />
        ) : (
          <div className="flex h-full items-center justify-center text-sm text-muted-foreground">No photo</div>
        )}
      </div>
      <div className="p-4">
        <h3 className="line-clamp-2 font-semibold text-foreground">{getItemDisplayName(item)}</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          {getItemDisplayBrand(item) || 'Unknown brand'}
          {getItemDisplayScale(item) ? ` · ${getItemDisplayScale(item)}` : ''}
        </p>
        <p className="mt-2 inline-flex rounded-full bg-primary/10 px-2 py-1 text-xs font-medium capitalize text-primary">{item.condition}</p>
      </div>
    </article>
  );
}

function CollectionsPanel({
  collections,
  pagination,
  username,
  currentPage,
  onPageChange,
}: {
  collections: Collection[];
  pagination: ItemPagination;
  username: string;
  currentPage: number;
  onPageChange: (page: number) => void;
}) {
  if (collections.length === 0) {
    return <p className="py-10 text-center text-sm text-muted-foreground">No collections in this showcase yet.</p>;
  }

  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {collections.map((collection) => {
          const slug = collection.name.toLowerCase().replace(/[^a-z0-9]/g, '-');
          return (
            <Link key={collection.id} href={`/${username}/${slug}`} className="card-shell p-4 transition hover:shadow-md">
              <div className="flex items-center justify-between gap-3">
                <h3 className="line-clamp-2 font-semibold text-foreground">{collection.name}</h3>
                <span className={`pill ${collection.isPublic ? 'text-primary border-primary/35 bg-primary/10' : ''}`}>
                  {collection.isPublic ? 'Public' : 'Private'}
                </span>
              </div>
              {collection.description && <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">{collection.description}</p>}
              <p className="mt-4 text-xs uppercase tracking-[0.12em] text-muted-foreground">
                {collection.items.length} item{collection.items.length !== 1 ? 's' : ''}
              </p>
            </Link>
          );
        })}
      </div>
      {pagination.totalPages > 1 && (
        <div className="mt-6">
          <Pagination currentPage={currentPage} totalPages={pagination.totalPages} onPageChange={onPageChange} />
        </div>
      )}
    </>
  );
}

function Pagination({
  currentPage,
  totalPages,
  onPageChange,
}: {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}) {
  return (
    <div className="flex items-center justify-center gap-2">
      <button
        type="button"
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className="btn-ghost h-9 px-3 disabled:opacity-40"
      >
        Prev
      </button>
      <p className="text-sm text-muted-foreground">
        Page {currentPage} of {totalPages}
      </p>
      <button
        type="button"
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className="btn-ghost h-9 px-3 disabled:opacity-40"
      >
        Next
      </button>
    </div>
  );
}
